import Cropper from "react-cropper";
import { Box, Flex } from "@chakra-ui/react";
import { useTranslation } from "@shared/utils/internationalization";
import { useCallback, useState } from "react";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import Icon from "@ui-kit/icons/Icon.web";
import Button from "@ui-kit/form/Button/Button.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Center from "@ui-kit/layout/Center/Center.web";
import CDNImage from "@ui-kit/media/CDNImage/CDNImage.web";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { toBase64 } from "@shared/utils/file-utils/utils";
import GenericUploadInput from "../GenericUploadInput/GenericUploadInput.web";
import { ImageUploadInputPropsType } from "./ImageUploadInput.props";
import { useImageUploadInput } from "./useImageUploadInput";

const notifier = NotifierFactory.createNotifier();

/**
 * Avatar variant component - circular profile image with edit overlay
 */
function AvatarUploadInput({
  id,
  image,
  updateImage,
  maxFileSize,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  handleErrorCodes,
  avatarSize = 150,
  editButtonAriaLabel,
}: ImageUploadInputPropsType) {
  const { t } = useTranslation("common");
  const [imageLoaded, setImageLoaded] = useState(false);

  const acceptedFileTypes = {
    "image/png": [".png"],
    "image/jpeg": [".jpeg", ".jpg"],
  };

  const onDrop = (fileList: File[], fileRejections: FileRejection[]) => {
    const img = new Image();

    try {
      img.src = URL.createObjectURL(fileList[0]);

      img.onload = async () => {
        const { naturalHeight, naturalWidth } = img;

        if (naturalWidth < minWidth || naturalHeight < minHeight) {
          notifier.error(handleErrorCodes["file-min-dimensions"] || "");
          return;
        }

        if (naturalWidth > maxWidth || naturalHeight > maxHeight) {
          notifier.error(handleErrorCodes["file-max-dimensions"] || "");
          return;
        }
        const imgSrc = await toBase64(fileList[0]);
        updateImage(imgSrc.toString());
      };
    } catch {
      updateImage(undefined);

      let tooManyFilesAlerts = false;

      fileRejections.forEach((rejection) => {
        rejection.errors.forEach((error) => {
          if (error.code === ErrorCode.TooManyFiles && tooManyFilesAlerts) {
            return;
          }

          if (error.code === ErrorCode.TooManyFiles) {
            tooManyFilesAlerts = true;
          }

          const errorMessage =
            handleErrorCodes[error.code as keyof typeof handleErrorCodes];
          if (errorMessage) {
            notifier.error(errorMessage);
          }
        });
      });
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxSize: maxFileSize,
    minSize: undefined,
    accept: acceptedFileTypes,
    maxFiles: 1,
    multiple: false,
    noDrag: true,
  });

  return (
    <Center data-sentry-mask>
      <Flex
        {...getRootProps()}
        position="relative"
        cursor="pointer"
        _hover={{
          "& img": {
            filter: "brightness(80%)",
          },
        }}
        css={{
          "& img": {
            filter: "brightness(85%)",
          },
        }}
      >
        <input {...getInputProps()} id={id} />
        <IconButton
          aria-label={editButtonAriaLabel || t("Edit image")}
          iconName="Edit"
          iconColor="white"
          size="lg"
          variant="ghost"
          position="absolute"
          bottom="calc(50% - 20px)"
          left="calc(50% - 20px)"
          zIndex={100}
          backgroundColor="transparent"
          _hover={{ backgroundColor: "transparent" }}
          pointerEvents="none"
        />
        <CDNImage
          src={image || ""}
          alt={t("Profile image")}
          width={avatarSize}
          height={avatarSize}
          style={{
            borderRadius: "50%",
            display: imageLoaded || image ? "block" : "none",
            objectFit: "cover",
          }}
          onLoad={() => setImageLoaded(true)}
        />
        {!image && !imageLoaded && (
          <Flex
            width={`${avatarSize}px`}
            height={`${avatarSize}px`}
            borderRadius="50%"
            backgroundColor="gray.200"
            alignItems="center"
            justifyContent="center"
          >
            <Icon.User boxSize="element.4xl" color="gray.400" />
          </Flex>
        )}
      </Flex>
    </Center>
  );
}

/**
 * Default dropzone variant component
 */
function DropzoneUploadInput(props: ImageUploadInputPropsType) {
  const { maxWidth, maxHeight, cropAspectRatio, image, updateImage } = props;

  const aspectRatio = cropAspectRatio ?? maxWidth / maxHeight;

  const {
    onDrop,
    finishCropping,
    updateCurrentFile,
    restartUpload,
    currentFile,
    isCropping,
    acceptedFileTypes,
    cropperRef,
  } = useImageUploadInput(props);

  const { t } = useTranslation("common");

  const handleCropperReady = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const cropBoxData = cropper.getCropBoxData();
      const imageData = cropper.getImageData();

      const zoomRatio = Math.min(
        cropBoxData.width / imageData.naturalWidth,
        cropBoxData.height / imageData.naturalHeight
      );

      cropper.zoomTo(zoomRatio);

      setTimeout(() => {
        const newImageData = cropper.getImageData();
        const newCropBoxData = cropper.getCropBoxData();

        cropper.setCanvasData({
          left:
            newCropBoxData.left +
            (newCropBoxData.width - newImageData.width) / 2,
          top:
            newCropBoxData.top +
            (newCropBoxData.height - newImageData.height) / 2,
        });
      }, 0);
    }
  }, [cropperRef]);

  return (
    <>
      {!isCropping && (
        <GenericUploadInput
          {...props}
          acceptedFileTypes={acceptedFileTypes}
          onDrop={onDrop}
          dropzoneIcon={
            <Icon.Image.Outline boxSize="element.5xl" color="gray.400" />
          }
          document={image}
          updateDocument={updateImage}
          currentFile={currentFile}
          updateCurrentFile={updateCurrentFile}
          aspectRatio={aspectRatio}
        />
      )}
      {image && isCropping && (
        <Box width="100%">
          <Cropper
            ref={cropperRef}
            src={image}
            style={{ height: 400, width: "100%" }}
            initialAspectRatio={aspectRatio}
            aspectRatio={aspectRatio}
            minCropBoxHeight={50}
            minCropBoxWidth={50}
            responsive
            guides
            checkOrientation={false}
            background={false}
            viewMode={0}
            dragMode="move"
            autoCropArea={0.8}
            ready={handleCropperReady}
          />

          <Flex width="100%" pt="2xs">
            <Button
              data-testid="image-upload-restart"
              ml="auto"
              colorPalette="primary"
              variant="outline"
              onClick={restartUpload}
            >
              {t("Restart")}
            </Button>
            <Button
              data-testid="image-upload-finish-cropping"
              colorPalette="primary"
              variant="solid"
              ml="xs"
              onClick={finishCropping}
            >
              {t("Finish Cropping")}
            </Button>
          </Flex>
        </Box>
      )}
    </>
  );
}

const ImageUploadInput = (props: ImageUploadInputPropsType) => {
  const { variant = "default" } = props;

  if (variant === "avatar") {
    return <AvatarUploadInput {...props} />;
  }

  return <DropzoneUploadInput {...props} />;
};

export default ImageUploadInput;
